import { Fragment } from 'react'
import { Card, ErrorBox, Loading, Empty, SourceTag } from './ui'
import { money, num, pct, negClass } from './format'

// Rows in the "Per order" section: orders is a count, the rest are $/order.
function isCount(row) {
  const k = String(row.key || '')
  return k === 'orders' || k === 'orders_shipped' || /^orders?_count$/.test(k)
}
function isPerOrder(row) {
  return /per_order/.test(String(row.key || ''))
}

function fmt(row, v) {
  if (isCount(row)) return num(v)
  return money(v, isPerOrder(row) ? 2 : 0)
}

export default function StatementTab({ query }) {
  const { data, error, isLoading } = query
  if (error) return <ErrorBox error={error} what="the statement" />
  if (isLoading && !data) return <Card title="Statement"><Loading /></Card>
  const cols = data?.columns || []
  const rows = data?.rows || []
  if (!cols.length || !rows.length) return <Card title="Statement"><Empty>No figures for this period yet.</Empty></Card>

  const curCls = (c) => (c.is_current ? 'bg-blue-50' : '')

  return (
    <Card
      title="Statement"
      sub="Ex-GST. Revenue counts on the day the work happened (ship date, activity date). * = still open, so figures are provisional. — = not known yet."
    >
      <div className="flex flex-wrap gap-3.5 text-xs text-slate-500 mb-2 items-center">
        <span><SourceTag source="portal" /> from the portal</span>
        <span><SourceTag source="xero" /> from Xero</span>
        <span><SourceTag source="pass_through" /> charged at cost: adds to revenue and cost equally</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr>
              <th className="text-left px-2.5 py-2 border-b border-slate-300" />
              {cols.map((c) => (
                <th
                  key={c.key}
                  title={c.from && c.to ? `${c.from} to ${c.to}` : undefined}
                  className={`text-right font-semibold text-slate-500 text-xs px-2.5 py-2 border-b border-slate-300 whitespace-nowrap ${curCls(c)}`}
                >
                  {c.label}{c.provisional ? ' *' : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              if (r.type === 'section') {
                return (
                  <tr key={`s-${i}`}>
                    <td colSpan={cols.length + 1} className="font-semibold text-slate-800 bg-slate-50 border-t border-slate-200 px-2.5 py-1.5">
                      {r.label}
                    </td>
                  </tr>
                )
              }
              const isKey = r.type === 'key'
              const isTotal = r.type === 'total'
              const trCls = isKey
                ? 'font-bold text-[14px] bg-green-50 border-t-2 border-green-300'
                : isTotal ? 'font-bold border-t-2 border-slate-300' : ''
              return (
                <tr key={r.key || `r-${i}`} className={trCls}>
                  <td className={`text-left px-2.5 py-1.5 border-b border-slate-100 ${r.type === 'line' ? 'pl-6 text-slate-700' : ''}`}>
                    {r.label}<SourceTag source={r.source} />
                  </td>
                  {cols.map((c, ci) => {
                    const v = r.values?.[ci]
                    const p = r.pct?.[ci]
                    const cur = c.is_current ? (isKey ? 'bg-green-100' : 'bg-blue-50') : ''
                    return (
                      <td key={c.key} className={`text-right px-2.5 py-1.5 border-b border-slate-100 tabular-nums whitespace-nowrap ${cur} ${isKey ? negClass(v) : ''}`}>
                        {fmt(r, v)}
                        {Array.isArray(r.pct) && (
                          <Fragment>{' '}<span className="text-xs font-normal text-slate-500">{pct(p)}</span></Fragment>
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2 leading-relaxed">
        Wages, super and overheads come from Xero. Interest and income tax are left out, which is what makes the
        bottom line PBIT.
      </p>
    </Card>
  )
}
